// Canonical hubCMD logo — "Command Pulse" PNG (matches favicon & all marketing materials).
// SVG variants below are reserved for the logo-showcase / variant explorer only.
import logoPng from "../assets/command-pulse.png";

export default function HubCmdLogo({ size = 36, variant = "shield", withWordmark = false, className = "", inverted = false }) {
  const ember = "#ff4500";
  const ember2 = "#ff5f15";
  const carbon = inverted ? "#ffffff" : "#1a1a1a";
  const padded = withWordmark ? "inline-flex items-center gap-2" : "inline-block";
  // Canonical "shield" variant uses the PNG mark for brand consistency with the favicon.
  if (variant === "shield") {
    return (
      <span className={`${padded} ${className}`}>
        <img src={logoPng} alt="hubCMD" width={size} height={size}
          style={{ objectFit: "contain", display: "block",
            filter: inverted ? "drop-shadow(0 0 0.5px rgba(255,255,255,0.4))" : undefined }} />
        {withWordmark && (
          <span className="font-display font-black tracking-tight leading-none" style={{ fontSize: size * 0.55 }}>
            <span style={{ color: carbon }}>hub</span><span style={{ color: ember }}>CMD</span>
          </span>
        )}
      </span>
    );
  }
  const Mark = LOGO_VARIANTS[variant] || LOGO_VARIANTS.hexSolid;
  return (
    <span className={`${padded} ${className}`}>
      <Mark size={size} ember={ember} ember2={ember2} carbon={carbon} />
      {withWordmark && (
        <span className="font-display font-black tracking-tight leading-none" style={{ fontSize: size * 0.55 }}>
          <span style={{ color: carbon }}>hub</span><span style={{ color: ember }}>CMD</span>
        </span>
      )}
    </span>
  );
}

// SVG showcase variants (20 alt concepts; not used on the live website)
export const LOGO_VARIANTS = {
  // 2: Filled hex H (now the SVG-based fallback)
  hexSolid: ({ size, ember }) => (
    <svg width={size} height={size} viewBox="0 0 64 64" fill="none">
      <path d="M32 4 L56 18 L56 46 L32 60 L8 46 L8 18 Z" fill={ember} />
      <path d="M22 18 L22 46 M42 18 L42 46 M22 32 L42 32" stroke="#fff" strokeWidth="5" strokeLinecap="round" />
    </svg>
  ),
  // 3: Atom-orbit H (image 1 middle)
  orbit: ({ size, ember }) => (
    <svg width={size} height={size} viewBox="0 0 64 64" fill="none">
      <ellipse cx="32" cy="32" rx="26" ry="14" stroke={ember} strokeWidth="3" />
      <ellipse cx="32" cy="32" rx="14" ry="26" stroke={ember} strokeWidth="3" />
      <circle cx="32" cy="6" r="3.5" fill={ember} />
      <circle cx="32" cy="58" r="3.5" fill={ember} />
      <circle cx="6" cy="32" r="3.5" fill={ember} />
      <circle cx="58" cy="32" r="3.5" fill={ember} />
      <path d="M24 22 L24 42 M40 22 L40 42 M24 32 L40 32" stroke={ember} strokeWidth="4" strokeLinecap="round" />
    </svg>
  ),
  // 4: Ascending bars + check (image 1 right)
  ascend: ({ size, ember }) => (
    <svg width={size} height={size} viewBox="0 0 64 64" fill="none">
      <path d="M14 56 L14 28 L22 28 L22 56 Z" fill={ember} />
      <path d="M28 56 L28 18 L36 18 L36 56 Z" fill={ember} />
      <path d="M42 56 L42 8 L50 8 L50 56 Z" fill={ember} />
      <path d="M12 56 L52 56 L46 62 L18 62 Z" fill={ember} opacity="0.7" />
    </svg>
  ),
  // 5: iOS rounded-square H+check (image 2)
  iosTile: ({ size, ember }) => (
    <svg width={size} height={size} viewBox="0 0 64 64" fill="none">
      <rect x="6" y="6" width="52" height="52" rx="14" fill={ember} />
      <path d="M20 16 L20 48 M40 16 L40 48 M20 32 L40 32" stroke="#fff" strokeWidth="5" strokeLinecap="round" />
      <path d="M34 40 L40 46 L50 32" stroke="#fff" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  // 6: Isometric cube (image 3)
  cube: ({ size, ember, ember2, carbon }) => (
    <svg width={size} height={size} viewBox="0 0 64 64" fill="none">
      <path d="M32 6 L56 18 L56 46 L32 58 L8 46 L8 18 Z" stroke={carbon} strokeWidth="1.5" fill="none" />
      <path d="M32 6 L56 18 L32 30 L8 18 Z" fill={ember} />
      <path d="M32 30 L56 18 L56 46 L32 58 Z" fill={ember} opacity="0.85" />
      <path d="M32 30 L8 18 L8 46 L32 58 Z" fill={carbon} />
    </svg>
  ),
  // 7: Gear+H (lean/ISO)
  gear: ({ size, ember }) => (
    <svg width={size} height={size} viewBox="0 0 64 64" fill="none">
      <g stroke={ember} strokeWidth="3" fill="none">
        <circle cx="32" cy="32" r="22" />
        {[0,45,90,135,180,225,270,315].map((a) => (
          <rect key={a} x="29" y="2" width="6" height="8" transform={`rotate(${a} 32 32)`} fill={ember} />
        ))}
      </g>
      <path d="M22 20 L22 44 M42 20 L42 44 M22 32 L42 32" stroke={ember} strokeWidth="5" strokeLinecap="round" />
    </svg>
  ),
  // 8: Bolt H
  bolt: ({ size, ember }) => (
    <svg width={size} height={size} viewBox="0 0 64 64" fill="none">
      <path d="M22 8 L22 30 L14 30 L34 60 L34 38 L42 38 L22 8 Z" fill={ember} />
      <path d="M48 6 L48 58" stroke={ember} strokeWidth="6" strokeLinecap="round" />
    </svg>
  ),
  // 9: Square H stamp
  stamp: ({ size, ember }) => (
    <svg width={size} height={size} viewBox="0 0 64 64" fill="none">
      <rect x="8" y="8" width="48" height="48" rx="4" fill={ember} />
      <path d="M22 18 L22 46 M42 18 L42 46 M22 32 L42 32" stroke="#fff" strokeWidth="6" strokeLinecap="square" />
    </svg>
  ),
  // 10: Outline H with corner brackets
  bracket: ({ size, ember, carbon }) => (
    <svg width={size} height={size} viewBox="0 0 64 64" fill="none">
      <path d="M8 8 L20 8 M8 8 L8 20" stroke={ember} strokeWidth="3" />
      <path d="M56 8 L44 8 M56 8 L56 20" stroke={ember} strokeWidth="3" />
      <path d="M8 56 L20 56 M8 56 L8 44" stroke={ember} strokeWidth="3" />
      <path d="M56 56 L44 56 M56 56 L56 44" stroke={ember} strokeWidth="3" />
      <path d="M22 20 L22 44 M42 20 L42 44 M22 32 L42 32" stroke={carbon} strokeWidth="5" strokeLinecap="round" />
    </svg>
  ),
  // 11: Circle H badge
  circleBadge: ({ size, ember }) => (
    <svg width={size} height={size} viewBox="0 0 64 64" fill="none">
      <circle cx="32" cy="32" r="28" fill={ember} />
      <path d="M22 18 L22 46 M42 18 L42 46 M22 32 L42 32" stroke="#fff" strokeWidth="6" strokeLinecap="round" />
    </svg>
  ),
  // 12: Diamond H
  diamond: ({ size, ember }) => (
    <svg width={size} height={size} viewBox="0 0 64 64" fill="none">
      <path d="M32 4 L60 32 L32 60 L4 32 Z" stroke={ember} strokeWidth="4" />
      <path d="M22 22 L22 42 M42 22 L42 42 M22 32 L42 32" stroke={ember} strokeWidth="4" strokeLinecap="round" />
    </svg>
  ),
  // 13: Quality checkmark
  qualityCheck: ({ size, ember }) => (
    <svg width={size} height={size} viewBox="0 0 64 64" fill="none">
      <circle cx="32" cy="32" r="28" stroke={ember} strokeWidth="4" />
      <path d="M16 34 L26 44 L48 22" stroke={ember} strokeWidth="6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  // 14: H + flag
  flag: ({ size, ember }) => (
    <svg width={size} height={size} viewBox="0 0 64 64" fill="none">
      <path d="M14 4 L14 60" stroke={ember} strokeWidth="4" strokeLinecap="round" />
      <path d="M16 8 L52 8 L46 18 L52 28 L16 28 Z" fill={ember} />
      <path d="M28 36 L28 58 M44 36 L44 58 M28 47 L44 47" stroke={ember} strokeWidth="5" strokeLinecap="round" />
    </svg>
  ),
  // 15: Process arrows (PDCA)
  pdca: ({ size, ember, ember2 }) => (
    <svg width={size} height={size} viewBox="0 0 64 64" fill="none">
      <g stroke={ember} strokeWidth="4" fill="none" strokeLinecap="round">
        <path d="M14 32 A18 18 0 0 1 32 14" />
        <path d="M32 14 A18 18 0 0 1 50 32" />
        <path d="M50 32 A18 18 0 0 1 32 50" />
        <path d="M32 50 A18 18 0 0 1 14 32" />
      </g>
      <path d="M32 8 L36 14 L28 14 Z" fill={ember} />
      <text x="32" y="36" textAnchor="middle" fontSize="14" fontWeight="900" fill={ember2} fontFamily="Chivo">H</text>
    </svg>
  ),
  // 16: Funnel / value stream
  funnel: ({ size, ember }) => (
    <svg width={size} height={size} viewBox="0 0 64 64" fill="none">
      <path d="M4 12 L60 12 L40 32 L40 58 L24 58 L24 32 Z" stroke={ember} strokeWidth="4" strokeLinejoin="round" />
      <path d="M14 20 L50 20" stroke={ember} strokeWidth="3" strokeLinecap="round" />
    </svg>
  ),
  // 17: H + dot grid (5S)
  dotGrid: ({ size, ember }) => (
    <svg width={size} height={size} viewBox="0 0 64 64" fill="none">
      {[0,1,2].flatMap((y) => [0,1,2].map((x) => (
        <circle key={`${x}-${y}`} cx={12 + x*20} cy={12 + y*20} r="3" fill={ember} opacity={x + y === 0 ? 1 : 0.4} />
      )))}
      <path d="M22 20 L22 44 M42 20 L42 44 M22 32 L42 32" stroke={ember} strokeWidth="5" strokeLinecap="round" />
    </svg>
  ),
  // 18: H with shield + tick
  shieldCheck: ({ size, ember }) => (
    <svg width={size} height={size} viewBox="0 0 64 64" fill="none">
      <path d="M32 4 L56 12 L56 32 C56 46 44 56 32 60 C20 56 8 46 8 32 L8 12 Z" fill={ember} />
      <path d="M20 32 L29 41 L46 24" stroke="#fff" strokeWidth="5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  // 19: Wave H
  wave: ({ size, ember }) => (
    <svg width={size} height={size} viewBox="0 0 64 64" fill="none">
      <path d="M4 44 Q16 28 32 44 T60 44" stroke={ember} strokeWidth="4" fill="none" strokeLinecap="round" />
      <path d="M22 14 L22 38 M42 14 L42 38 M22 26 L42 26" stroke={ember} strokeWidth="5" strokeLinecap="round" />
    </svg>
  ),
  // 20: Stencil H block
  stencil: ({ size, ember, carbon }) => (
    <svg width={size} height={size} viewBox="0 0 64 64" fill="none">
      <rect x="8" y="8" width="48" height="48" fill={carbon} />
      <path d="M22 12 L22 52 M42 12 L42 52 M22 32 L42 32" stroke={ember} strokeWidth="6" strokeLinecap="square" />
      <rect x="6" y="6" width="52" height="4" fill={ember} />
      <rect x="6" y="54" width="52" height="4" fill={ember} />
    </svg>
  ),
};

export const LOGO_ORDER = [
  "hexSolid", "orbit", "ascend", "iosTile",
  "cube", "gear", "bolt", "stamp", "bracket",
  "circleBadge", "diamond", "qualityCheck", "flag", "pdca",
  "funnel", "dotGrid", "shieldCheck", "wave", "stencil",
];

export const LOGO_LABELS = {
  hexSolid: "02 · Filled Hex",
  orbit: "03 · Atom Orbit",
  ascend: "04 · Ascending Bars",
  iosTile: "05 · iOS Tile",
  cube: "06 · Isometric Cube",
  gear: "07 · Quality Gear",
  bolt: "08 · Energy Bolt",
  stamp: "09 · Block Stamp",
  bracket: "10 · Corner Brackets",
  circleBadge: "11 · Circle Badge",
  diamond: "12 · Diamond Cut",
  qualityCheck: "13 · QA Tick",
  flag: "14 · Standard Flag",
  pdca: "15 · PDCA Cycle",
  funnel: "16 · Value Stream",
  dotGrid: "17 · 5S Dot Grid",
  shieldCheck: "18 · Audit Shield",
  wave: "19 · Flow Wave",
  stencil: "20 · Factory Stencil",
};
